// WEL-5236 single-order contract (pure)

export const MAX_GIFT_ORDER_QUANTITY = 99;

export type SubmitDonationParams = {
  donationAmount: number;
  homeDonationBusinessId?: number;
  homeInstitutionId: number;
  registrationNo: string;
  region?: string;
  productNumber?: string;
  optionItemIds?: number[];
  addressId?: number;
  deliveryMessage?: string;
  isOfflineProduct?: boolean;
  quantity?: number;
};

export const buildProcessDonationBody = (
  { quantity = 1, ...params }: SubmitDonationParams,
  pinkey?: string,
) => {
  if (params.productNumber && params.isOfflineProduct && !params.addressId) {
    throw new Error('오프라인 상품은 배송지를 선택해야 합니다.');
  }

  return {
    registrationNo: params.registrationNo,
    homeInstitutionId: params.homeInstitutionId,
    donationAmount: params.donationAmount,
    homeDonationBusinessId: params.homeDonationBusinessId,
    pinkey: pinkey || undefined,
    region: params.region,
    productNumber: params.productNumber,
    optionItemIds: params.optionItemIds,
    quantity: params.productNumber ? quantity : undefined,
    addressId: params.addressId,
    deliveryMessage: params.deliveryMessage,
  };
};

export type DonationProductInfo = {
  productNumber: string;
  productName: string;
  imageUrl?: string;
  institutionName?: string;
  institutionLogoUrl?: string;
  optionItemIds?: number[];
  optionName?: string;
  minDonationAmount: number;
  isOfflineProduct: boolean;
};

export type GiftProductSource = {
  productName: string;
  mainImageUrl?: string | null;
  institutionName?: string | null;
  institutionLogoUrl?: string | null;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  optionGroupInfoList?:
    | {
        optionItemInfoList?:
          | {
              optionItemId: number;
              itemName?: string | null;
              extraPrice?: number | null;
            }[]
          | null;
      }[]
    | null;
};

const CUSTOM_DELIVERY_OPTION = '직접 입력';

export const parseOptionItemIds = (
  raw: string | null | undefined,
): number[] | undefined => {
  if (!raw) {
    return undefined;
  }

  const optionItemIds = raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .map(Number)
    .filter(
      (optionItemId) => Number.isInteger(optionItemId) && optionItemId > 0,
    );

  return optionItemIds.length > 0 ? optionItemIds : undefined;
};

export const parseOrderQuantity = (
  raw: string | null | undefined,
): number | undefined => {
  if (!raw) {
    return undefined;
  }

  const quantity = Number(raw);
  if (!Number.isInteger(quantity)) {
    return undefined;
  }

  return Math.min(Math.max(quantity, 1), MAX_GIFT_ORDER_QUANTITY);
};

export const serializeOptionItemIds = (
  optionItemIds: number[],
): string | undefined => {
  const uniqueOptionItemIds = [...new Set(optionItemIds)];

  return uniqueOptionItemIds.length > 0
    ? uniqueOptionItemIds.join(',')
    : undefined;
};

/** 주문서(`/donation/order`)는 `donationBusinessId` 또는 `institutionId`+`institutionName` 중 하나를 반드시 받는다 — 타입으로 표현되지 않는 제약이다. */
export const buildSingleOrderSearchParams = ({
  donationBusinessId,
  institutionId,
  institutionName,
  initialAmount,
  initialPurpose,
  productNumber,
  optionItemIds = [],
  quantity = 1,
}: {
  donationBusinessId?: number;
  institutionId?: number;
  institutionName?: string;
  initialAmount?: number;
  initialPurpose?: string;
  productNumber?: string;
  optionItemIds?: number[];
  quantity?: number;
}) => ({
  donationBusinessId,
  institutionId,
  institutionName,
  initialAmount,
  initialPurpose,
  productNumber,
  optionItemIds: serializeOptionItemIds(optionItemIds),
  quantity: productNumber ? quantity : undefined,
});

export const buildDonationProductInfo = (
  productNumber: string,
  giftProduct: GiftProductSource,
  optionItemIds?: number[],
): DonationProductInfo => {
  const selectedOptionItems = (giftProduct.optionGroupInfoList ?? []).flatMap(
    (optionGroup) =>
      optionGroup.optionItemInfoList?.filter((optionItem) =>
        optionItemIds?.includes(optionItem.optionItemId),
      ) ?? [],
  );

  const optionExtraPriceTotal = selectedOptionItems.reduce(
    (total, optionItem) => total + (optionItem.extraPrice ?? 0),
    0,
  );

  const optionName =
    selectedOptionItems
      .map((optionItem) => optionItem.itemName)
      .filter(Boolean)
      .join(' / ') || undefined;

  return {
    productNumber,
    productName: giftProduct.productName,
    imageUrl: giftProduct.mainImageUrl ?? undefined,
    institutionName: giftProduct.institutionName ?? undefined,
    institutionLogoUrl: giftProduct.institutionLogoUrl ?? undefined,
    optionItemIds,
    optionName,
    minDonationAmount:
      giftProduct.requiredDonationAmount + optionExtraPriceTotal,
    isOfflineProduct: !giftProduct.onlineProductYN,
  };
};

export const resolveDeliveryMessage = (deliveryMessage: {
  option: string;
  customText?: string;
}): string | undefined => {
  if (deliveryMessage.option === CUSTOM_DELIVERY_OPTION) {
    return deliveryMessage.customText || undefined;
  }

  return deliveryMessage.option || undefined;
};

export const hasAllRequiredOptions = (
  selectedOptionGroupIds: number[],
  requiredOptionGroupIds: number[],
): boolean => {
  if (requiredOptionGroupIds.length === 0) {
    return true;
  }

  const selectedGroupIds = new Set(selectedOptionGroupIds);

  return requiredOptionGroupIds.every((optionGroupId) =>
    selectedGroupIds.has(optionGroupId),
  );
};

export const isDonationSubmitDisabled = ({
  notAvailable,
  donationAmount,
  hasPurpose,
  hasInvalidUnit,
  minimumRequiredAmount,
  isAddressMissing,
}: {
  notAvailable: boolean;
  donationAmount: number;
  hasPurpose: boolean;
  hasInvalidUnit: boolean;
  minimumRequiredAmount: number;
  isAddressMissing: boolean;
}): boolean => {
  return (
    notAvailable ||
    !donationAmount ||
    !hasPurpose ||
    hasInvalidUnit ||
    donationAmount < minimumRequiredAmount ||
    isAddressMissing
  );
};

//* 진입 수량만큼 곱하지 않으면 최소 기부금액에 미달해 제출 버튼이 처음부터 잠긴다.
export const resolveInitialDonationAmount = ({
  initialAmount,
  unitMinimumAmount,
  initialQuantity,
  fallbackAmount,
}: {
  initialAmount?: number;
  unitMinimumAmount?: number;
  initialQuantity: number;
  fallbackAmount: number;
}): number => {
  if (initialAmount !== undefined) {
    return initialAmount;
  }

  if (unitMinimumAmount === undefined) {
    return fallbackAmount;
  }

  return unitMinimumAmount * initialQuantity;
};

//* 최소금액에 딱 붙어 있던 금액만 수량을 따라간다 — 사용자가 직접 얹은 금액은 수량 조작이 깎지 않는다.
export const resolveAmountForQuantityChange = ({
  currentAmount,
  unitMinimumAmount,
  currentQuantity,
  nextQuantity,
}: {
  currentAmount: number;
  unitMinimumAmount: number;
  currentQuantity: number;
  nextQuantity: number;
}): number => {
  const nextMinimumAmount = unitMinimumAmount * nextQuantity;

  if (currentAmount > unitMinimumAmount * currentQuantity) {
    return Math.max(currentAmount, nextMinimumAmount);
  }

  return nextMinimumAmount;
};
